import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import confetti from 'canvas-confetti';
import UserAvatar from '@/components/UserAvatar';

const MEDAL = ['', '#FFD700', '#C0C0C0', '#CD7F32'];
const MEDAL_LABEL = ['', '1', '2', '3'];

const dispararConfete = () => {
  const duration = 4000;
  const end = Date.now() + duration;
  const fire = () => {
    confetti({ particleCount: 80, angle: 60, spread: 70, origin: { x: 0, y: 0.7 }, colors: ['#FFD700', '#FFA500', '#FF4500', '#00FF00', '#00BFFF'] });
    confetti({ particleCount: 80, angle: 120, spread: 70, origin: { x: 1, y: 0.7 }, colors: ['#FFD700', '#FFA500', '#FF4500', '#00FF00', '#00BFFF'] });
    if (Date.now() < end) requestAnimationFrame(fire);
  };
  fire();
};

const falarParabens = (nome, status) => {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const msg = status === 'Instalada'
    ? `Parabéns, ${nome}! Mais uma venda instalada! Vamos buscar a próxima!`
    : `Parabéns, ${nome}! Mais uma venda gerada!`;
  const utter = new SpeechSynthesisUtterance(msg);
  utter.lang = 'pt-BR';
  utter.rate = 1;
  utter.pitch = 1.1;
  utter.volume = 1;
  const voices = window.speechSynthesis.getVoices();
  const ptVoice = voices.find(v => v.lang.startsWith('pt'));
  if (ptVoice) utter.voice = ptVoice;
  window.speechSynthesis.speak(utter);
};

const RankingPresencial = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroInicio, setFiltroInicio] = useState('');
  const [filtroFim, setFiltroFim] = useState('');
  const [mostrarFiltro, setMostrarFiltro] = useState(false);
  const [agora, setAgora] = useState(new Date());
  const [celebracao, setCelebracao] = useState(null);
  const anunciadosRef = useRef(new Set());
  const [equipes, setEquipes] = useState([]);
  const [equipeId, setEquipeId] = useState('');

  // Carregar equipes do supervisor
  useEffect(() => {
    if (user?.perfil === 'SUPERVISOR' || user?.perfil === 'ADMIN' || user?.perfil === 'GERENTE') {
      api.get('/equipes').then(res => {
        const lista = res.data || [];
        setEquipes(lista);
        // Supervisor: auto-selecionar equipe onde é supervisor
        if (user?.perfil === 'SUPERVISOR') {
          const minhaEquipe = lista.find(e => e.supervisor_id === user.id);
          if (minhaEquipe) setEquipeId(minhaEquipe.id);
        }
      }).catch(() => {});
    }
  }, [user]);

  const fetchRanking = useCallback(async () => {
    try {
      const params = { tipo_atuacao: 'Presencial' };
      if (filtroInicio) params.data_inicio = filtroInicio;
      if (filtroFim) params.data_fim = filtroFim;
      if (equipeId) params.equipe_id = equipeId;
      const res = await api.get('/ranking/vendedores', { params });
      setData(res.data);
    } catch (err) {
      console.error('Erro ao carregar ranking:', err);
    } finally {
      setLoading(false);
    }
  }, [filtroInicio, filtroFim, equipeId]);

  useEffect(() => {
    fetchRanking();
    const interval = setInterval(() => {
      fetchRanking();
      setAgora(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchRanking]);

  // Polling de eventos para comemoracao por voz
  const fetchEventos = useCallback(async () => {
    try {
      const res = await api.get('/ranking/eventos-recentes', { params: { tipo_atuacao: 'Presencial' } });
      const eventos = res.data || [];
      for (const ev of eventos) {
        if (!anunciadosRef.current.has(ev.id)) {
          anunciadosRef.current.add(ev.id);
          setCelebracao({ nome: ev.vendedor_nome, status: ev.status });
          falarParabens(ev.vendedor_nome, ev.status);
          dispararConfete();
          setTimeout(() => setCelebracao(null), 8000);
          api.put(`/ranking/eventos/${ev.id}/anunciado`).catch(() => {});
          break;
        }
      }
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchEventos();
    const evInterval = setInterval(fetchEventos, 15000);
    return () => clearInterval(evInterval);
  }, [fetchEventos]);

  const formatMoeda = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  const formatPeriodo = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="animate-pulse text-red-400 text-3xl font-bold tracking-wider">CARREGANDO RANKING...</div>
      </div>
    );
  }

  const ranking = data?.ranking || [];
  const top = data?.top_por_produto || {};
  const periodo = data?.periodo || {};

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white p-4 md:p-6 overflow-hidden" data-testid="ranking-presencial-page">
      {/* Banner de Celebracao */}
      {celebracao && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-500 text-black py-4 px-6 text-center animate-pulse shadow-2xl" data-testid="celebracao-banner">
          <p className="text-2xl md:text-4xl font-black">
            Parabens, {celebracao.nome}! Mais uma venda {celebracao.status.toLowerCase()}!
          </p>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-purple-400 to-purple-400">
            RANKING PRESENCIAL
          </h1>
          <p className="text-gray-400 mt-1 text-sm md:text-base">
            {formatPeriodo(periodo.inicio)} - {formatPeriodo(periodo.fim)} | Atualizado: {agora.toLocaleTimeString('pt-BR')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {user?.perfil === 'ADMIN' && (
            <a href="/ranking-homeoffice" className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-semibold transition-colors" data-testid="link-ranking-ho">
              Home Office
            </a>
          )}
          <button
            onClick={() => setMostrarFiltro(!mostrarFiltro)}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 border border-gray-700 transition-colors"
            data-testid="btn-filtro-ranking"
          >
            Filtrar Periodo
          </button>
          <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" title="Auto-refresh ativo (30s)" />
        </div>
      </div>

      {/* Filtro */}
      {mostrarFiltro && (
        <div className="mb-4 flex items-end gap-3 p-4 bg-gray-900/80 rounded-xl border border-gray-800 flex-wrap">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Data Inicio</label>
            <input type="date" value={filtroInicio} onChange={e => setFiltroInicio(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Data Fim</label>
            <input type="date" value={filtroFim} onChange={e => setFiltroFim(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <button onClick={fetchRanking} className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold transition-colors">Aplicar</button>
          <button onClick={() => { setFiltroInicio(''); setFiltroFim(''); setMostrarFiltro(false); }} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 border border-gray-700 transition-colors">Limpar</button>
          {user && equipes.length > 0 && user.perfil !== 'SUPERVISOR' && (
            <div>
              <label className="text-xs text-gray-400 block mb-1">Equipe</label>
              <select value={equipeId} onChange={e => setEquipeId(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="filtro-equipe-ranking">
                <option value="">Todas</option>
                {equipes.map(eq => <option key={eq.id} value={eq.id}>{eq.nome}</option>)}
              </select>
            </div>
          )}
          {user?.perfil === 'SUPERVISOR' && equipeId && (
            <div className="flex items-center gap-2 px-3 py-2 bg-yellow-600/20 rounded-lg border border-yellow-600/40">
              <span className="text-xs text-yellow-400 font-semibold">Equipe: {equipes.find(e => e.id === equipeId)?.nome || ''}</span>
            </div>
          )}
        </div>
      )}

      {/* Top por Produto */}
      {ranking.length > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Internet', vendedor: top.internet, color: 'from-blue-600 to-blue-800' },
            { label: 'TV', vendedor: top.tv, color: 'from-purple-600 to-purple-800' },
            { label: 'Fixo', vendedor: top.fixo, color: 'from-green-600 to-green-800' },
            { label: 'Movel', vendedor: top.movel, color: 'from-orange-600 to-orange-800' },
          ].map(({ label, vendedor, color }) => (
            <div key={label} className={`bg-gradient-to-br ${color} rounded-xl p-3 text-center border border-white/10`}>
              <p className="text-xs text-white/70 uppercase tracking-wide">Top {label}</p>
              <p className="font-bold text-sm md:text-base truncate mt-1">{vendedor || '-'}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabela */}
      {ranking.length === 0 ? (
        <div className="text-center py-20 text-gray-500 text-xl">Nenhuma venda instalada no periodo para vendedores Presencial</div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-800/60">
          <table className="w-full" data-testid="ranking-presencial-table">
            <thead>
              <tr className="bg-gray-900/90 border-b border-gray-800">
                <th className="text-center p-3 text-xs uppercase tracking-wider text-gray-400 w-16">Rank</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-gray-400">Vendedor</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider text-blue-400">Instaladas</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider text-red-400">Canceladas</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider text-yellow-400">Pend. Inst.</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider text-green-400">Receita</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider text-blue-400">Internet</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider text-purple-400">TV</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider text-red-400">Fixo</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider text-orange-400">Movel</th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((v, i) => {
                const pos = i + 1;
                const isTop3 = pos <= 3;
                return (
                  <tr key={v.vendedor_id} className={`border-b border-gray-800/40 transition-colors ${isTop3 ? 'bg-gray-900/60' : 'hover:bg-gray-900/40'}`}>
                    <td className="text-center p-3">
                      {isTop3 ? (
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-black" style={{ backgroundColor: MEDAL[pos], color: '#0a0e1a' }}>
                          {MEDAL_LABEL[pos]}
                        </span>
                      ) : (
                        <span className="text-gray-500 font-bold text-lg">{pos}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <UserAvatar fotoPath={v.foto_path} nome={v.vendedor_nome} size={isTop3 ? 'lg' : 'md'} />
                        <span className={`font-bold ${isTop3 ? 'text-lg text-white' : 'text-base text-gray-300'}`}>{v.vendedor_nome}</span>
                      </div>
                    </td>
                    <td className="text-center p-3"><span className="text-xl font-black text-blue-400">{v.instaladas}</span></td>
                    <td className="text-center p-3"><span className="text-lg font-bold text-red-400">{v.canceladas || 0}</span></td>
                    <td className="text-center p-3"><span className="text-lg font-bold text-yellow-400">{v.pendente_instalacao || 0}</span></td>
                    <td className="text-center p-3"><span className="font-bold text-green-400">{formatMoeda(v.receita)}</span></td>
                    <td className="text-center p-3"><span className="text-lg font-bold text-blue-400">{v.internet}</span></td>
                    <td className="text-center p-3"><span className="text-lg font-bold text-purple-400">{v.tv}</span></td>
                    <td className="text-center p-3"><span className="text-lg font-bold text-red-400">{v.fixo}</span></td>
                    <td className="text-center p-3"><span className="text-lg font-bold text-orange-400">{v.movel}</span></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-purple-900/40 border-t-2 border-purple-500">
                <td className="text-center p-3" colSpan={2}><span className="text-lg font-black text-white uppercase">TOTAL</span></td>
                <td className="text-center p-3"><span className="text-xl font-black text-blue-300">{ranking.reduce((s, v) => s + v.instaladas, 0)}</span></td>
                <td className="text-center p-3"><span className="text-xl font-black text-red-300">{ranking.reduce((s, v) => s + (v.canceladas || 0), 0)}</span></td>
                <td className="text-center p-3"><span className="text-xl font-black text-yellow-300">{ranking.reduce((s, v) => s + (v.pendente_instalacao || 0), 0)}</span></td>
                <td className="text-center p-3"><span className="text-lg font-black text-green-300">{formatMoeda(ranking.reduce((s, v) => s + v.receita, 0))}</span></td>
                <td className="text-center p-3"><span className="text-xl font-black text-blue-300">{ranking.reduce((s, v) => s + v.internet, 0)}</span></td>
                <td className="text-center p-3"><span className="text-xl font-black text-purple-300">{ranking.reduce((s, v) => s + v.tv, 0)}</span></td>
                <td className="text-center p-3"><span className="text-xl font-black text-red-300">{ranking.reduce((s, v) => s + v.fixo, 0)}</span></td>
                <td className="text-center p-3"><span className="text-xl font-black text-orange-300">{ranking.reduce((s, v) => s + v.movel, 0)}</span></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="mt-4 text-center text-gray-600 text-xs">
        MULTI TELECOM - Ranking Presencial atualizado automaticamente a cada 30 segundos
      </div>
    </div>
  );
};

export default RankingPresencial;
